import { ByteArrayInput, ByteArrayOutput, isDefined, isInstanceOf, isNotNull, Option, RuntimeNotifier } from "@opendaw/lib-std";
import { Pointers } from "@opendaw/studio-enums";
import { ModulationBox, NoteEventCollectionBox, RootBox, TrackBox, ValueEventCollectionBox } from "@opendaw/studio-boxes";
import { DeviceBoxUtils, DeviceHost, Devices, isModulatorBox, UnionBoxTypes } from "@opendaw/studio-adapters";
import { ClipboardUtils } from "../ClipboardUtils";
// The chain kind an effect device box belongs in.
const effectAccepts = (box) => box.tags.deviceType === "audio-effect" ? "audio" : "midi";
export var DevicesClipboard;
(function (DevicesClipboard) {
    const encodeMetadata = (metadata) => {
        const output = ByteArrayOutput.create();
        output.writeBoolean(metadata.hasInstrument);
        output.writeString(metadata.instrumentContent);
        output.writeInt(metadata.midiEffectCount);
        output.writeInt(metadata.midiEffectMaxIndex);
        output.writeInt(metadata.audioEffectCount);
        output.writeInt(metadata.audioEffectMaxIndex);
        return output.toArrayBuffer();
    };
    const decodeMetadata = (buffer) => {
        const input = new ByteArrayInput(buffer);
        return {
            hasInstrument: input.readBoolean(),
            instrumentContent: input.readString(),
            midiEffectCount: input.readInt(),
            midiEffectMaxIndex: input.readInt(),
            audioEffectCount: input.readInt(),
            audioEffectMaxIndex: input.readInt()
        };
    };
    // Every box a set of selected devices OWNS and depends on, for the clipboard. The owned subtree is walked
    // RECURSIVELY through mandatory incoming edges — a composite's entry cells, the effects nested inside them,
    // deeper composites and their chains — and each box in it contributes its mandatory deps and preserved
    // resources (so a nested effect's soundfont / NN model comes along too). A one-level walk that stopped at
    // the entry cells, plus the `isDeviceBox` exclusion, previously dropped everything hosted in a composite
    // entry. Excludes device boxes and the RootBox from the OUTGOING dep walks; the audio-unit timeline is
    // bundled separately (only alongside an instrument).
    DevicesClipboard.collectDeviceDependencies = (deviceBoxes, boxGraph) => {
        const collectOwned = (root) => {
            const owned = [];
            const seen = new Set();
            const visit = (box) => box.incomingEdges().forEach(pointer => {
                if (!pointer.mandatory || pointer.box.ephemeral || isDefined(pointer.box.resource)
                    || seen.has(pointer.box)) {
                    return;
                }
                seen.add(pointer.box);
                owned.push(pointer.box);
                visit(pointer.box);
            });
            visit(root);
            return owned;
        };
        return deviceBoxes.flatMap(box => {
            const owned = collectOwned(box);
            // A ModulationBox's `source` leaves the owned subtree: the modulator's `assignments` field is not
            // mandatory, so the dependency walk stops before the modulator. Take the modulator box ALONE (never as
            // a walk root: `dependenciesOf` follows incoming mandatory edges, which would drag in that modulator's
            // assignments to OTHER devices), otherwise a paste into another project has nothing to point at.
            const modulators = owned.flatMap(candidate => isInstanceOf(candidate, ModulationBox)
                ? candidate.source.targetVertex.mapOr(vertex => [vertex.box], []) : []);
            const roots = [box, ...owned];
            const mandatoryDeps = roots.flatMap(root => Array.from(boxGraph.dependenciesOf(root, {
                alwaysFollowMandatory: true,
                stopAtResources: true,
                excludeBox: (dep) => dep.ephemeral || DeviceBoxUtils.isDeviceBox(dep)
                    || dep.name === RootBox.ClassName
            }).boxes).filter(dep => dep.resource !== "preserved"));
            const preserved = roots.flatMap(root => Array.from(boxGraph.dependenciesOf(root, {
                alwaysFollowMandatory: true,
                excludeBox: (dep) => dep.ephemeral || DeviceBoxUtils.isDeviceBox(dep)
            }).boxes).filter(dep => dep.resource === "preserved"));
            return [...owned, ...modulators, ...mandatoryDeps, ...preserved];
        });
    };
    DevicesClipboard.findRootBox = (boxGraph) => Option.wrap(boxGraph.boxes().find(box => isInstanceOf(box, RootBox)));
    // The bundle carries the modulator so a paste into ANOTHER project gets a copy. Pasting into the project the
    // copy came from must not add a second one: the existing modulator is dropped from the paste here, and
    // `mapModulationPointer` then keeps the assignment pointing at it.
    DevicesClipboard.excludeExistingModulator = (box, boxGraph) => isModulatorBox(box) && boxGraph.findBox(box.address.uuid).nonEmpty();
    // Only reached for a pointer the uuid remap did not answer, i.e. a target outside the pasted set. A `source`
    // whose modulator is already in this graph keeps its address (same project). A `collection` belongs to a
    // modulator copy that just landed in another project and has to join THAT project's root.
    DevicesClipboard.mapModulationPointer = (pointer, address, boxGraph) => {
        if (pointer.pointerType === Pointers.ModulatorSource) {
            return address.flatMap(target => boxGraph.findVertex(target).map(() => target));
        }
        if (pointer.pointerType === Pointers.ModulatorCollection) {
            return DevicesClipboard.findRootBox(boxGraph).map(rootBox => rootBox.modulators.address);
        }
        return Option.None;
    };
    // Place the freshly pasted effects into the destination chain at the insert index. ONLY top-level effects
    // (hosted by the destination chain field) are re-indexed; effects nested inside a pasted composite entry are
    // hosted by their entry, keep the in-entry index they were copied with, and must NOT compete for a top-level
    // slot — otherwise the composite itself lands at a wrong index (e.g. after the following device).
    DevicesClipboard.reindexPastedTopLevelEffects = (pastedBoxes, audioChainAddress, midiChainAddress, audioInsertIndex, midiInsertIndex) => {
        const hostedByChain = (box, chain) => chain.mapOr(address => box.host.targetVertex
            .mapOr(vertex => vertex.address.equals(address), false), false);
        const effects = pastedBoxes.filter(DeviceBoxUtils.isEffectDeviceBox);
        const newMidi = effects
            .filter(box => box.tags.deviceType === "midi-effect" && hostedByChain(box, midiChainAddress))
            .sort((a, b) => a.index.getValue() - b.index.getValue());
        const newAudio = effects
            .filter(box => box.tags.deviceType === "audio-effect" && hostedByChain(box, audioChainAddress))
            .sort((a, b) => a.index.getValue() - b.index.getValue());
        newMidi.forEach((box, idx) => box.index.setValue(midiInsertIndex + idx));
        newAudio.forEach((box, idx) => box.index.setValue(audioInsertIndex + idx));
    };
    // Duplicate the current device selection in place (copy then paste through an internal clipboard entry,
    // leaving the OS clipboard untouched). Paste inserts the copies right after the selection and re-selects them.
    DevicesClipboard.duplicate = (context) => {
        const handler = DevicesClipboard.createHandler(context);
        handler.copy().ifSome(entry => handler.paste(entry));
    };
    DevicesClipboard.createHandler = ({ getEnabled, editing, selection, boxGraph, boxAdapters, getHost }) => {
        const isCopyable = (adapter) => adapter.box.tags.copyable !== false;
        const copyableSelected = () => selection.selected().filter(isCopyable);
        const copyDevices = () => {
            // Seal any pending UI-state (e.g. the editing-chain pointer set when entering a composite branch) as
            // its own history step, so the following PASTE does not fold it into the paste's undo entry — undoing
            // the paste must remove the pasted device without also navigating out of the branch.
            editing.mark();
            const selected = copyableSelected();
            if (selected.length === 0) {
                return Option.None;
            }
            let instrument = null;
            const midiEffects = [];
            const audioEffects = [];
            for (const adapter of selected) {
                if (adapter.type === "instrument") {
                    instrument = adapter;
                }
                else if (adapter.type === "midi-effect") {
                    midiEffects.push(adapter);
                }
                else if (adapter.type === "audio-effect") {
                    audioEffects.push(adapter);
                }
            }
            if (instrument === null && midiEffects.length === 0 && audioEffects.length === 0) {
                return Option.None;
            }
            midiEffects.sort((a, b) => a.indexField.getValue() - b.indexField.getValue());
            audioEffects.sort((a, b) => a.indexField.getValue() - b.indexField.getValue());
            const midiEffectMaxIndex = midiEffects.length > 0
                ? midiEffects[midiEffects.length - 1].indexField.getValue()
                : 0;
            const audioEffectMaxIndex = audioEffects.length > 0
                ? audioEffects[audioEffects.length - 1].indexField.getValue()
                : 0;
            const deviceBoxes = [
                ...(instrument !== null ? [instrument.box] : []),
                ...midiEffects.map(adapter => adapter.box),
                ...audioEffects.map(adapter => adapter.box)
            ];
            const dependencies = DevicesClipboard.collectDeviceDependencies(deviceBoxes, boxGraph);
            const trackContent = [];
            if (isNotNull(instrument)) {
                getHost().ifSome(host => {
                    const tracksField = host.audioUnitBoxAdapter().tracksField;
                    for (const pointer of tracksField.pointerHub.filter(Pointers.TrackCollection)) {
                        if (!isInstanceOf(pointer.box, TrackBox)) {
                            continue;
                        }
                        const track = pointer.box;
                        trackContent.push(track);
                        for (const regionPointer of track.regions.pointerHub.incoming()) {
                            trackContent.push(regionPointer.box);
                            const regionDeps = Array.from(boxGraph.dependenciesOf(regionPointer.box, {
                                alwaysFollowMandatory: true,
                                stopAtResources: true,
                                excludeBox: (dep) => dep.ephemeral
                                    || isInstanceOf(dep, TrackBox)
                                    || DeviceBoxUtils.isDeviceBox(dep)
                            }).boxes);
                            trackContent.push(...regionDeps);
                        }
                    }
                });
            }
            const allBoxes = [...deviceBoxes, ...dependencies, ...trackContent];
            const instrumentContent = isNotNull(instrument)
                ? instrument.box.tags.content ?? ""
                : "";
            const metadata = {
                hasInstrument: isNotNull(instrument),
                instrumentContent,
                midiEffectCount: midiEffects.length,
                midiEffectMaxIndex,
                audioEffectCount: audioEffects.length,
                audioEffectMaxIndex
            };
            const data = ClipboardUtils.serializeBoxes(allBoxes, encodeMetadata(metadata));
            return Option.wrap({ type: "devices", data, count: selected.length });
        };
        return {
            canCopy: () => getEnabled() && copyableSelected().length > 0,
            canCut: () => getEnabled() && copyableSelected().length > 0,
            canPaste: (entry) => getEnabled() && entry.type === "devices",
            copy: copyDevices,
            cut: () => {
                const result = copyDevices();
                result.ifSome(() => {
                    const optHost = getHost();
                    if (optHost.isEmpty()) {
                        return;
                    }
                    const host = optHost.unwrap();
                    const selected = new Set(selection.selected().filter(adapter => adapter.type !== "instrument"));
                    const remainingMidi = host.midiEffects
                        .mapOr(chain => chain.adapters().filter(adapter => !selected.has(adapter)), []);
                    const remainingAudio = host.audioEffects
                        .mapOr(chain => chain.adapters().filter(adapter => !selected.has(adapter)), []);
                    editing.modify(() => {
                        selected.forEach(adapter => adapter.box.delete());
                        remainingMidi.forEach((adapter, index) => adapter.indexField.setValue(index));
                        remainingAudio.forEach((adapter, index) => adapter.indexField.setValue(index));
                    });
                });
                return result;
            },
            paste: (entry) => {
                if (entry.type !== "devices" || !getEnabled()) {
                    return;
                }
                const optHost = getHost();
                if (optHost.isEmpty()) {
                    return;
                }
                const host = optHost.unwrap();
                const metadata = decodeMetadata(ClipboardUtils.extractMetadata(entry.data));
                const selected = selection.selected();
                const selectedInstrument = selected.find(adapter => adapter.type === "instrument");
                const selectedMidiEffects = selected.filter(adapter => adapter.type === "midi-effect");
                const selectedAudioEffects = selected.filter(adapter => adapter.type === "audio-effect");
                let replaceInstrument = metadata.hasInstrument && isDefined(selectedInstrument)
                    && selectedInstrument.box.tags.copyable !== false;
                if (replaceInstrument && isDefined(selectedInstrument)) {
                    const selectedContent = selectedInstrument.box.tags.content;
                    if (isDefined(selectedContent) && metadata.instrumentContent !== ""
                        && selectedContent !== metadata.instrumentContent) {
                        RuntimeNotifier.info({
                            headline: "Incompatible Instrument",
                            message: `Cannot replace a '${selectedContent}' instrument with a '${metadata.instrumentContent}' instrument.`
                        }).finally();
                        replaceInstrument = false;
                    }
                }
                const midiInsertIndex = selectedMidiEffects.length > 0
                    ? selectedMidiEffects.reduce((max, adapter) => Math.max(max, adapter.indexField.getValue()), -1) + 1
                    : 0;
                const audioInsertIndex = selectedAudioEffects.length > 0
                    ? selectedAudioEffects.reduce((max, adapter) => Math.max(max, adapter.indexField.getValue()), -1) + 1
                    : 0;
                editing.modify(() => {
                    selection.deselectAll();
                    if (replaceInstrument && isDefined(selectedInstrument)) {
                        const tracksField = host.audioUnitBoxAdapter().tracksField;
                        for (const pointer of tracksField.pointerHub.filter(Pointers.TrackCollection)) {
                            if (isInstanceOf(pointer.box, TrackBox)) {
                                pointer.box.delete();
                            }
                        }
                        selectedInstrument.box.delete();
                    }
                    host.midiEffects.ifSome(chain => {
                        for (const adapter of chain.adapters()) {
                            const currentIndex = adapter.indexField.getValue();
                            if (currentIndex >= midiInsertIndex) {
                                adapter.indexField.setValue(currentIndex + metadata.midiEffectCount);
                            }
                        }
                    });
                    host.audioEffects.ifSome(chain => {
                        for (const adapter of chain.adapters()) {
                            const currentIndex = adapter.indexField.getValue();
                            if (currentIndex >= audioInsertIndex) {
                                adapter.indexField.setValue(currentIndex + metadata.audioEffectCount);
                            }
                        }
                    });
                    const boxes = ClipboardUtils.deserializeBoxes(entry.data, boxGraph, {
                        mapPointer: (pointer, address) => {
                            if (address.isEmpty()) {
                                return Option.None;
                            }
                            if (pointer.pointerType === Pointers.InstrumentHost && replaceInstrument) {
                                return Option.wrap(host.inputField.address);
                            }
                            if (pointer.pointerType === Pointers.MIDIEffectHost) {
                                return host.midiEffectsField.map(field => field.address);
                            }
                            if (pointer.pointerType === Pointers.AudioEffectHost) {
                                return host.audioEffectsField.map(field => field.address);
                            }
                            if (pointer.pointerType === Pointers.TrackCollection) {
                                return Option.wrap(host.audioUnitBoxAdapter().tracksField.address);
                            }
                            if (pointer.pointerType === Pointers.Automation && replaceInstrument) {
                                return Option.wrap(host.audioUnitBoxAdapter().address);
                            }
                            if (pointer.pointerType === Pointers.MIDIDevice) {
                                return DevicesClipboard.findRootBox(boxGraph).map(rootBox => rootBox.outputMidiDevices.address);
                            }
                            return DevicesClipboard.mapModulationPointer(pointer, address, boxGraph);
                        },
                        keepUuid: isModulatorBox,
                        excludeBox: box => {
                            if (DevicesClipboard.excludeExistingModulator(box, boxGraph)) {
                                return true;
                            }
                            // A ONE-SIDED host (a composite entry) takes only ONE chain kind. An effect of the
                            // other kind has nowhere to attach: `mapPointer` finds no host field, so its
                            // mandatory `host` would dangle and reject the whole transaction (as the timeline
                            // subtree below would). Drop it rather than paste a broken box. Checked BEFORE the
                            // replaceInstrument bail: that case must not smuggle an unattachable effect in.
                            if (DeviceBoxUtils.isEffectDeviceBox(box)
                                && DeviceHost.chainFieldOf(host, effectAccepts(box)).isEmpty()) {
                                return true;
                            }
                            if (replaceInstrument) {
                                return false;
                            }
                            if (DeviceBoxUtils.isInstrumentDeviceBox(box)) {
                                return true;
                            }
                            // The unit's timeline content (tracks + their regions/clips/event-collections)
                            // is bundled only alongside an instrument, for the REPLACE case. When an
                            // instrument is present but we are NOT replacing, none of it may be pasted: a
                            // region deserialized without its excluded track dangles its mandatory `regions`
                            // pointer and rejects the whole transaction (#1049-#1051). Drop the entire
                            // timeline subtree, not just the TrackBox. (An effect-only copy has no
                            // instrument, so its automation tracks/regions still paste.)
                            if (metadata.hasInstrument
                                && (isInstanceOf(box, TrackBox)
                                    || UnionBoxTypes.isRegionBox(box)
                                    || UnionBoxTypes.isClipBox(box)
                                    || isInstanceOf(box, NoteEventCollectionBox)
                                    || isInstanceOf(box, ValueEventCollectionBox))) {
                                return true;
                            }
                            return false;
                        }
                    });
                    const deviceBoxes = boxes.filter(box => DeviceBoxUtils.isDeviceBox(box));
                    DevicesClipboard.reindexPastedTopLevelEffects(boxes, host.audioEffectsField.map(field => field.address), host.midiEffectsField.map(field => field.address), audioInsertIndex, midiInsertIndex);
                    const tracksField = host.audioUnitBoxAdapter().tracksField;
                    const allTracks = tracksField.pointerHub.filter(Pointers.TrackCollection)
                        .filter(pointer => isInstanceOf(pointer.box, TrackBox))
                        .map(pointer => pointer.box)
                        .sort((trackA, trackB) => trackA.index.getValue() - trackB.index.getValue());
                    allTracks.forEach((track, idx) => track.index.setValue(idx));
                    selection.select(...deviceBoxes.map(box => boxAdapters.adapterFor(box, Devices.isAny)));
                });
            }
        };
    };
})(DevicesClipboard || (DevicesClipboard = {}));
